import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ArrowDown } from 'lucide-react'
import { Tile } from '@/components/Tile'
import { Prose } from '@/components/Prose'
import { LawBadge, DesignBadge, DivergenceBadge, AutomationDot } from '@/components/honesty'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import { t } from '@/lib/ui'
import { ICON_BY_SLUG } from '@/lib/site'
import { frontPage } from '@/content/fi/front-page'

// Area 0 — the front page (DESIGN-BRIEF §2, FI). Copy is N0's front-page-fi.md,
// placed verbatim. This surface owns: what ArkkiKELA is and is not (the
// manifesto), the disclaimer, the invitation to walk, and the two-truths
// promise (NARRATIVE-SPINE §5). It argues nothing else.
export function FrontPage() {
  useDocumentTitle()

  return (
    <div className="mx-auto max-w-shell px-4">
      {/* --- Hero / headline block (above the fold) --- */}
      <section className="border-b border-hairline py-16 sm:py-20">
        <h1 className="text-5xl font-semibold tracking-tight text-ink sm:text-6xl">
          {frontPage.title}
        </h1>
        <p className="mt-4 max-w-prose text-xl leading-snug text-ink sm:text-2xl">
          {frontPage.subtitle}
        </p>
        <Prose className="mt-6" >{frontPage.lead}</Prose>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to={frontPage.ctaPrimary.href}
            className="inline-flex items-center gap-2 rounded bg-accent px-5 py-2.5 font-medium text-paper transition-opacity hover:opacity-90"
          >
            {frontPage.ctaPrimary.label}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <a
            href={frontPage.ctaSecondary.href}
            className="inline-flex items-center gap-2 rounded border border-hairline px-5 py-2.5 font-medium text-ink transition-colors hover:border-accent hover:text-accent"
          >
            {frontPage.ctaSecondary.label}
            <ArrowDown className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* --- Manifesto --- */}
      <section id="manifesti" className="scroll-mt-28 py-14">
        <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted">
          {frontPage.manifestoHeading}
        </p>
        <Prose variant="narrative">{frontPage.manifesto}</Prose>
      </section>

      {/* --- Two-truths legend (the honesty overlay, front and centre) --- */}
      <TwoTruthsLegend />

      {/* --- Featured band: the synthetic population (WP-S1). A prominent linked
              companion, deliberately NOT a peer tile — it is the data model made
              flesh, a different kind of surface from the eight model areas. --- */}
      <FeaturedBand />

      {/* --- Four large tiles (FI areas) --- */}
      <section className="py-14">
        <div className="grid gap-4 sm:grid-cols-2">
          {frontPage.largeTiles.map((tile) => (
            <Tile
              key={tile.slug}
              to={tile.slug}
              title={tile.title}
              blurb={tile.blurb}
              icon={ICON_BY_SLUG[tile.slug]}
              size="large"
            />
          ))}
        </div>
      </section>

      {/* --- Secondary tiles (data model, ArkkiValtio — the more technical FI
              surfaces, visually subordinate). --- */}
      <section className="border-t border-hairline py-14">
        <div className="grid gap-3 sm:grid-cols-2">
          {frontPage.smallTiles.map((tile) => (
            <Tile
              key={tile.slug}
              to={tile.slug}
              title={tile.title}
              blurb={tile.blurb}
              icon={ICON_BY_SLUG[tile.slug]}
              size="small"
              langNote={tile.langNote}
            />
          ))}
        </div>
      </section>
    </div>
  )
}

// The featured band — the synthetic population, given prominence above the tile
// grid without being flattened into a peer tile (WP-S1 IA decision).
function FeaturedBand() {
  const f = frontPage.featured
  const Icon = ICON_BY_SLUG[f.slug]
  return (
    <section className="py-8">
      <Link
        to={f.slug}
        className="group flex flex-col gap-4 rounded border border-accent bg-accent-soft p-6 transition-colors hover:bg-accent-soft/70 sm:flex-row sm:items-center sm:p-8"
      >
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-accent/40 bg-paper">
          {Icon && <Icon className="h-6 w-6 text-accent" aria-hidden="true" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">{f.kicker}</p>
          <h2 className="mt-0.5 text-2xl font-semibold tracking-tight text-ink">{f.title}</h2>
          <p className="mt-1.5 max-w-prose text-muted">{f.blurb}</p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-2 self-start rounded bg-accent px-4 py-2 font-medium text-paper transition-opacity group-hover:opacity-90 sm:self-center">
          {f.cta}
          <ArrowRight className="h-4 w-4 -translate-x-0.5 transition-transform group-hover:translate-x-0" aria-hidden="true" />
        </span>
      </Link>
    </section>
  )
}

// The two-truths marks with their meanings. Generic here (the marks and what
// they mean); the sample page shows them on real content. Every mark is
// clickable — its popover explains the distinction.
function TwoTruthsLegend() {
  return (
    <section className="rounded border border-hairline bg-paper p-6 sm:p-8">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted">
        {t('frontPage.legend.kicker')}
      </p>
      <p className="mt-2 max-w-prose text-muted">{t('frontPage.legend.lead')}</p>

      <div className="mt-6 grid gap-8 sm:grid-cols-2">
        <div>
          <p className="mb-3 text-sm font-semibold text-ink">{t('frontPage.legend.originGroup')}</p>
          <ul className="space-y-3">
            <LegendRow name={t('honesty.law.name')}>
              <LawBadge />
            </LegendRow>
            <LegendRow name={t('honesty.design.name')}>
              <DesignBadge />
            </LegendRow>
            <LegendRow name={t('honesty.divergence.name')}>
              <DivergenceBadge />
            </LegendRow>
          </ul>
        </div>

        <div>
          <p className="mb-3 text-sm font-semibold text-ink">
            {t('frontPage.legend.decisionGroup')}
          </p>
          <ul className="space-y-3">
            <LegendRow name={t('honesty.auto.name')}>
              <AutomationDot level="auto" />
            </LegendRow>
            <LegendRow name={t('honesty.assisted.name')}>
              <AutomationDot level="assisted" />
            </LegendRow>
            <LegendRow name={t('honesty.humanLaw.name')}>
              <AutomationDot level="human-law" />
            </LegendRow>
            <LegendRow name={t('honesty.humanGap.name')}>
              <AutomationDot level="human-gap" />
            </LegendRow>
          </ul>
        </div>
      </div>
    </section>
  )
}

function LegendRow({ name, children }: { name: string; children: ReactNode }) {
  return (
    <li className="flex items-center gap-3">
      <span className="inline-flex w-16 shrink-0 justify-start">{children}</span>
      <span className="text-sm text-ink">{name}</span>
    </li>
  )
}
